import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
var _excluded = ["value", "suggestInterval", "onBlur", "onFocus", "onKeyDown", "onUpdate", "onClear", "isInvalidTime"];
import * as React from 'react';
import { addMinutes, getHours, subHours, addHours, startOfDay, format, setHours, setMinutes, isAfter } from 'date-fns';
import { isUndefined, max } from 'lodash-es';
import { isFalsy } from 'utility-types';
import { safeInvoke, keys, elementIsOrContains, isTruthy, isDefined } from '../../../../library';
import { Popover } from '../../../popover';
import { TagLink, Tag } from '../../../tag';
import { OptionList, OptionItem, selectClasses } from '../../select';
import { Input } from '../../text-input';
export var TimeInput = function TimeInput(props) {
  var _React$useState = React.useState(false),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    showSuggestion = _React$useState2[0],
    setShowSuggestion = _React$useState2[1];
  var _React$useState3 = React.useState(''),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    inputText = _React$useState4[0],
    setInputText = _React$useState4[1];
  var _React$useState5 = React.useState(false),
    _React$useState6 = _slicedToArray(_React$useState5, 2),
    hasUserInput = _React$useState6[0],
    setHasUserInput = _React$useState6[1];
  var targetRef = React.useRef(null);
  var optionListRef = React.useRef(null);
  var formatTime = function formatTime(time) {
    return isDefined(time) ? format(time, 'h:mm') : '';
  };
  var convertStringToTime = function convertStringToTime(time) {
    var isPm = !!props.value ? getHours(props.value) >= 12 : false;
    var returnDate = !!props.value ? props.value : new Date();
    var _inputText$split$map = inputText.split(':').map(function (t) {
        return parseInt(t, 10);
      }),
      _inputText$split$map2 = _slicedToArray(_inputText$split$map, 2),
      hour = _inputText$split$map2[0],
      minutes = _inputText$split$map2[1];
    if (isNaN(hour) || isNaN(minutes) || hour > 23 || minutes > 59) {
      throw new Error('not valid time string');
    }
    if (hour === 12) {
      hour = isPm ? 12 : 0;
    } else if (hour < 12 && isPm) {
      hour = hour + 12;
    }
    returnDate = setHours(returnDate, hour);
    returnDate = setMinutes(returnDate, minutes);
    return returnDate;
  };
  var timeOptions = React.useMemo(function () {
    var interval = max([props.suggestInterval, 5]);
    var startDate = !!props.value ? props.value : new Date();
    var perHour = 60 / interval;
    var perDay = 24 * perHour;
    var options = [];
    var time = startOfDay(startDate);
    for (var i = 0; i < perDay; i++) {
      options.push({
        id: format(time, 'HH:mm') + '_' + i,
        label: format(time, 'hh:mm a'),
        value: time
      });
      time = addMinutes(time, interval);
    }
    return options;
  }, [props.value, props.suggestInterval]);
  var getClosestOption = function getClosestOption(time) {
    if (!time) return null;
    var closestTime = null;
    for (var i = 0; i < timeOptions.length; i++) {
      if (isTruthy(safeInvoke(props.isInvalidTime, timeOptions[i].value))) {
        continue;
      } else if (!isAfter(timeOptions[i].value, time)) {
        closestTime = timeOptions[i];
      } else {
        break;
      }
    }
    return closestTime;
  };
  var isOptionDisabled = function isOptionDisabled(option) {
    var _safeInvoke;
    return (_safeInvoke = safeInvoke(props.isInvalidTime, option.value)) !== null && _safeInvoke !== void 0 ? _safeInvoke : false;
  };
  React.useEffect(function () {
    setInputText(props.value ? formatTime(props.value) : '');
  }, [props.value]);
  var togglePeriod = function togglePeriod(e) {
    e.preventDefault();
    if (isUndefined(props.value)) return;
    if (getHours(props.value) < 12) {
      safeInvoke(props.onUpdate, props.field, addHours(props.value, 12));
    } else {
      safeInvoke(props.onUpdate, props.field, subHours(props.value, 12));
    }
  };
  var setTimeFromInput = function setTimeFromInput() {
    if (!hasUserInput) return;
    var newTime;
    if (props.isClearable && inputText === '') {
      safeInvoke(props.onUpdate, props.field, undefined);
    } else {
      try {
        newTime = convertStringToTime(inputText);
        if (isFalsy(safeInvoke(props.isInvalidTime, newTime))) {
          safeInvoke(props.onUpdate, props.field, newTime);
        } else {
          throw new Error('Invalid Time');
        }
      } catch (error) {
        setInputText(formatTime(props.value));
      }
    }
    setHasUserInput(false);
  };
  var handleInputUpdate = function handleInputUpdate(field, value) {
    setInputText(value);
    setHasUserInput(true);
  };
  var handleInputFocus = function handleInputFocus(e) {
    setShowSuggestion(true);
    safeInvoke(props.onFocus, e);
  };
  var handleInputBlur = function handleInputBlur(e) {
    setTimeFromInput();
    safeInvoke(props.onBlur, e);
    requestAnimationFrame(function () {
      if (!elementIsOrContains(targetRef.current, document.activeElement)) {
        setShowSuggestion(false);
      }
    });
  };
  var handleTimeOptionSelect = function handleTimeOptionSelect(option) {
    safeInvoke(props.onUpdate, props.field, option.value);
    setShowSuggestion(false);
  };
  var handleInputKeyDown = function handleInputKeyDown(event) {
    var optionList = optionListRef.current;
    if (event.which === keys.ENTER) {
      if (!!optionList && !!optionList.state.focusedOption) {
        optionList.handleKeyDown(event);
      } else {
        setTimeFromInput();
      }
    } else if (event.which === keys.ARROW_DOWN) {
      setShowSuggestion(true);
      if (optionList) {
        optionList.handleKeyDown(event);
      }
    } else if (event.which === keys.ARROW_UP) {
      setShowSuggestion(true);
      if (optionList) {
        if (optionList.getFocusedIndex() === 0) {
          event.preventDefault();
          optionList.setFocusOption(null);
        } else {
          optionList.handleKeyDown(event);
        }
      }
    } else if (event.which === keys.ESCAPE) {
      setShowSuggestion(false);
    } else if (event.which === keys.TAB) {} else {
      setShowSuggestion(false);
    }
    safeInvoke(props.onKeyDown, event);
  };
  var handleInputClear = function handleInputClear() {
    safeInvoke(props.onUpdate, props.field, undefined);
    safeInvoke(props.onClear);
  };
  var handlePopoverOpening = function handlePopoverOpening() {
    var closestOption = isDefined(props.value) ? getClosestOption(props.value) : null;
    handlePopoverResize(null);
    if (optionListRef.current && closestOption) {
      optionListRef.current.setFocusOption(closestOption, function () {
        var _optionListRef$curren;
        (_optionListRef$curren = optionListRef.current) === null || _optionListRef$curren === void 0 || _optionListRef$curren.scrollFocusedItemToTop();
      });
    } else {
      var _optionListRef$curren2;
      (_optionListRef$curren2 = optionListRef.current) === null || _optionListRef$curren2 === void 0 || _optionListRef$curren2.setFocusOption(optionListRef.current.getNextFocusOption(1), function () {
        var _optionListRef$curren3;
        (_optionListRef$curren3 = optionListRef.current) === null || _optionListRef$curren3 === void 0 || _optionListRef$curren3.scrollFocusedItemToTop();
      });
    }
  };
  var handlePopoverResize = function handlePopoverResize(popup) {
    if (!!targetRef.current && !!popup) {
      popup.style.minWidth = targetRef.current.getBoundingClientRect().width + 'px';
    }
  };
  var renderPeriod = function renderPeriod(time) {
    if (isUndefined(time)) return null;
    if (props.isDisabled) {
      return React.createElement(Tag, {
        density: props.density,
        size: props.size,
        shape: props.shape,
        appearance: "minimal"
      }, format(time, 'a').toLowerCase());
    }
    return React.createElement(TagLink, {
      href: "#",
      density: props.density,
      size: props.size,
      shape: props.shape,
      appearance: "minimal",
      onClick: togglePeriod
    }, format(time, 'a').toLowerCase());
  };
  var renderOptions = function renderOptions() {
    return React.createElement(OptionList, {
      id: "".concat(props.field, "__list"),
      ref: optionListRef,
      options: timeOptions,
      optionIdProp: "id",
      onOptionSelect: handleTimeOptionSelect,
      optionDisabled: isOptionDisabled,
      renderOption: function renderOption(option, itemProps) {
        return React.createElement(OptionItem, {
          key: itemProps.id,
          id: itemProps.id,
          isActive: itemProps.modifiers.active,
          isDisabled: itemProps.modifiers.disabled,
          isFocused: itemProps.modifiers.focused,
          onClick: itemProps.handleClick,
          onMouseOver: itemProps.handleMouseEnter
        }, option.label);
      }
    });
  };
  var value = props.value,
    suggestInterval = props.suggestInterval,
    onBlur = props.onBlur,
    onFocus = props.onFocus,
    onKeyDown = props.onKeyDown,
    onUpdate = props.onUpdate,
    onClear = props.onClear,
    isInvalidTime = props.isInvalidTime,
    inputProps = _objectWithoutProperties(props, _excluded);
  return React.createElement(Popover, {
    isOpen: showSuggestion,
    autoFocus: false,
    enforceFocus: false,
    content: renderOptions(),
    position: "bottom",
    usePortal: false,
    onOpening: handlePopoverOpening,
    onResize: handlePopoverResize,
    isMinimal: true,
    className: selectClasses.OPTIONS,
    isDisabled: props.isDisabled || props.readOnly
  }, React.createElement(Input, _extends({}, inputProps, {
    value: inputText,
    after: renderPeriod(props.value),
    onBlur: handleInputBlur,
    onFocus: handleInputFocus,
    onKeyDown: handleInputKeyDown,
    onUpdate: handleInputUpdate,
    onClear: handleInputClear,
    ref: targetRef
  })));
};
TimeInput.defaultProps = {
  suggestInterval: 30,
  isInvalidTime: function isInvalidTime() {
    return false;
  },
  autoComplete: 'off'
};